import { LOCAL_HOST_CONFIG } from '@API/gambyt-data-services/temp-config'

import { useCallback, useEffect } from 'react'
import { useCookies } from 'react-cookie'
import { useSearchParams, useLocation } from 'react-router-dom'
import { AppConfig, AppConfigActions } from '@AppState/app-config'
import { useAppDispatch } from '@AppState/redux-store'
import { NAV_VALUES } from '@Components/navigation-bar/types'
import { NavigationActions } from '@AppState/navigation'
import { CONFIG_PARAMS } from './types'

const OPTIONAL_CONFIG_PARAMS = [CONFIG_PARAMS.PLAYER_ID]

export const AppConfigLoader = () => {
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()
  const { pathname } = useLocation()

  const hostName = 'mslc'

  const getCookieName = useCallback((prefix: string, key: CONFIG_PARAMS) => {
    return `${prefix}.${key}`
  }, [])

  const cookieNames = Object.values(CONFIG_PARAMS).map((param) => getCookieName(hostName, param))
  const [cookies] = useCookies([...cookieNames])
  const isLocalhost = useCallback(() => {
    return window.location.hostname === 'localhost'
  }, [])

  const isMaintenanceEnabled = useCallback(() => {
    return pathname === '/maintenance'
  }, [pathname])

  const getConfigValue = useCallback(
    function checkUrlThenCookie(paramName: CONFIG_PARAMS, defaultValue?: string) {
      if (searchParams.get(paramName)) {
        console.log(`🔵 Using Search Param for: ${paramName}`)
        return searchParams.get(paramName)
      } else {
        console.log(`🔴 Did not find search param ${paramName}`)
      }

      const cookieName = getCookieName(hostName, paramName)
      if (cookies[cookieName]) {
        console.log(`🍪 Using Cookie Param for: ${cookieName}`)
        return cookies[cookieName]
      } else {
        console.log(`🔴 Did not find cookie ${cookieName}`)
      }

      console.log(`🔴 Value not found for: ${paramName}`)
      if (isLocalhost()) {
        const retVal = defaultValue ?? LOCAL_HOST_CONFIG[hostName][paramName]
        console.log(`🔴 Using default value: ${retVal}`)
        return retVal
      }
      if (!OPTIONAL_CONFIG_PARAMS.includes(paramName)) {
        if (!isMaintenanceEnabled()) {
          throw Error(`${paramName} not found`)
        }
      }
    },
    [cookies, getCookieName, hostName, searchParams, isLocalhost, isMaintenanceEnabled],
  )

  useEffect(() => {
    console.log(`💠 Loading App Config for Host: ${hostName}`)
    const entryPoint = (() => {
      switch (searchParams.get(CONFIG_PARAMS.ENTRY)) {
        case NAV_VALUES.TICKET_MODE.name:
          return NAV_VALUES.TICKET_MODE
        case NAV_VALUES.VIEW_MODE.name:
          return NAV_VALUES.VIEW_MODE
        case NAV_VALUES.TICKET_HISTORY_MODE.name:
          return NAV_VALUES.TICKET_HISTORY_MODE
        default:
          return NAV_VALUES.VIEW_MODE
      }
    })()

    //Use Query Params, then cookies, then hard coded value
    const appConfig: AppConfig = {
      gameDataApiKey: 'x',
      gameDataServiceUrl: `${process.env.API_BASE}/api`,
      playerDataApiKey: 'x',
      playerDataServiceUrl: `${process.env.MSLC_PLAYER_BACKEND_BASE_URL}/api`,
      clientId: getConfigValue(CONFIG_PARAMS.CLIENT_ID),
      playerId: getConfigValue(CONFIG_PARAMS.PLAYER_ID),
      rapidGameId: 'keno-to-go',
      hostConfig: `/config/${hostName}.json`,
      hostName,
      underMaintenance: isMaintenanceEnabled(),
    }

    dispatch(AppConfigActions.setAppConfig(appConfig))
    dispatch(NavigationActions.setEntryPoint(entryPoint))
  }, [dispatch, getConfigValue, hostName, searchParams, isMaintenanceEnabled])

  return <></>
}
